/**
 * <s-product-recommendations> — "Você também pode gostar" row on the product page.
 * Products come from Shopify's recommendation engine (related intent, JSON from
 * routes.product_recommendations_url), never a collection slice.
 *
 * When the row nears the viewport it fetches data-json-url, clones <template data-recs-card>
 * for each available product (excluding the current one), and reveals [data-recs-section]
 * only when at least one card exists. With no recommendations (new store, no history) it
 * reveals the server-rendered [data-recs-fallback] list (Linha Essencial) instead.
 *
 * Attributes: data-json-url, data-product-id, data-limit, data-currency, data-locale.
 */
class SProductRecommendations extends HTMLElement {
  #ac;
  #observer;

  connectedCallback() {
    this.#ac = new AbortController();
    this.#observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.#observer.disconnect();
      this.#load();
    }, { rootMargin: '600px 0px' });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#ac?.abort();
  }

  #hasCards() {
    return Boolean(this.querySelector('[data-recs-json] li'));
  }

  #reveal() {
    const section = this.querySelector('[data-recs-section]');
    if (section) section.hidden = false;
  }

  async #load() {
    if (this.dataset.jsonUrl) {
      try {
        const response = await fetch(this.dataset.jsonUrl, {
          signal: this.#ac.signal,
          headers: { Accept: 'application/json' },
        });
        if (response.ok) {
          const data = await response.json();
          this.#renderJson(Array.isArray(data?.products) ? data.products : []);
        }
      } catch (error) {
        if (this.#ac.signal.aborted) return;
      }
    }

    if (!this.#hasCards()) {
      // No recommendation history (or the request failed): show the server-rendered fallback.
      const fallback = this.querySelector('[data-recs-fallback]');
      if (fallback && fallback.querySelector('li')) fallback.hidden = false;
    }
    if (this.#hasCards() || this.querySelector('[data-recs-fallback]:not([hidden]) li')) this.#reveal();
  }

  #renderJson(products) {
    const list = this.querySelector('[data-recs-json]');
    const template = this.querySelector('template[data-recs-card]');
    if (!list || !template) return;

    const currentId = Number(this.dataset.productId);
    const limit = Number(this.dataset.limit) || 4;
    const money = new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'pt-BR', {
      style: 'currency',
      currency: this.dataset.currency || 'BRL',
    });

    products
      .filter((product) => product && product.id !== currentId && product.available !== false)
      .slice(0, limit)
      .forEach((product) => {
        const card = template.content.cloneNode(true);
        card.querySelectorAll('[data-recs-link]').forEach((link) => {
          link.href = product.url;
        });
        const link = card.querySelector('a[data-recs-link]');
        if (link) link.setAttribute('aria-label', product.title);

        const image = card.querySelector('[data-recs-image]');
        if (image && product.featured_image) {
          const src = product.featured_image.startsWith('//') ? `https:${product.featured_image}` : product.featured_image;
          const url = new URL(src);
          url.searchParams.set('width', '600');
          image.src = url.toString();
          image.alt = product.title;
        } else if (image) {
          image.remove();
        }

        const isNew = Array.isArray(product.tags) && product.tags.includes('Novidade');
        const newBadge = card.querySelector('[data-recs-new]');
        if (newBadge && !isNew) newBadge.remove();

        const title = card.querySelector('[data-recs-title]');
        if (title) title.textContent = product.title;

        const price = card.querySelector('[data-recs-price]');
        if (price) price.textContent = money.format((product.price || 0) / 100);
        const compare = card.querySelector('[data-recs-compare]');
        if (compare) {
          if (product.compare_at_price && product.compare_at_price > product.price) {
            compare.textContent = money.format(product.compare_at_price / 100);
            compare.hidden = false;
            const savings = card.querySelector('[data-recs-savings]');
            const savingsTemplate = this.dataset.savingsTemplate;
            if (savings && savingsTemplate) {
              const amount = money.format((product.compare_at_price - product.price) / 100);
              savings.textContent = savingsTemplate.replace('__AMOUNT__', amount);
              savings.hidden = false;
            }
          }
        }

        list.append(card);
      });
  }
}

if (!customElements.get('s-product-recommendations')) {
  customElements.define('s-product-recommendations', SProductRecommendations);
}
